/**
 * Inventory-add receipt — bitmap renderer.
 *
 * Ports the layout of the legacy `generateInventoryReceiptString` (38-char text)
 * to the shared canvas toolkit: a record of products added in a bulk import.
 */
import { ProductDataType, BusinessDataV2Type } from '@am3nd-org/pointcraft-types';

import { fCurrency } from 'src/utils/format-number';

import type { PaperWidth } from './types';
import { createReceiptCanvas, appendBrandingFooter } from './canvas-toolkit';

export function inventoryReceiptCanvas(params: {
  items: ProductDataType[];
  business: BusinessDataV2Type;
  operator?: string;
  deviceId?: string;
  paperWidth?: PaperWidth;
}): string {
  const { items, business, operator, deviceId, paperWidth } = params;

  const c = createReceiptCanvas({ paperWidth });
  const { px } = c;

  const now = new Date();
  const totalValue = items.reduce((sum, p) => sum + p.price * (p.currentStockLevel ?? 1), 0);

  // ── Header ──────────────────────────────────────────────────────────────────
  c.lines(business.name, 34, 700, 'center');
  c.lines(business.businessInfo.address.street1, 24, 400, 'center');
  c.lines(
    `${business.businessInfo.address.city}, ${business.businessInfo.address.state} ${business.businessInfo.address.postalCode}`,
    24,
    400,
    'center'
  );
  c.space(px(8));
  c.lines(`${now.toLocaleDateString()}  ${now.toLocaleTimeString()}`, 22, 400, 'center');
  if (operator) c.lines(`Operator: ${operator}`, 22, 400, 'center');
  if (deviceId) c.lines(`Device: ${deviceId}`, 22, 400, 'center');
  c.rule();
  c.lines('INVENTORY ADD', 28, 700, 'center');
  c.rule();

  // ── Items: name + line value, then SKU · Qty underneath ──────────────────────
  items.forEach((p, idx) => {
    const qty = p.currentStockLevel ?? 1;
    c.itemRow(p.name, fCurrency(p.price * qty), 28, 600);
    c.lines(`SKU ${p.identifier.slice(-8)} · Qty ${qty}`, 22, 400, 'left', px(20));
    if (idx < items.length - 1) c.space(px(10));
  });

  // ── Totals ────────────────────────────────────────────────────────────────
  c.rule();
  c.row('Unique Items Added', String(items.length), 26, 600);
  c.row('Total Value', fCurrency(totalValue), 28, 700);

  appendBrandingFooter(c);
  return c.finish();
}
